import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

// Components
import ContactLabel from '@/views/components/misc/ContactLabel';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import { ComparisonMissingItemsModal, DeleteComparisonsModal } from '@/views/components/modals';
import { MenuItem } from '@/views/components/data/DataTableV2/TableMenu';
import { Button } from '@capintel/component-lib/button';
import { SearchInput } from '@/views/components/inputs';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import { COMPARISON_TABLE_COLUMNS_KEYS } from '@/enums/Comparison';

// Libraries
import { ColumnDef } from '@tanstack/react-table';
import { Dictionary } from 'lodash';

// Type
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import {
  closeComparison,
  DELETE_COMPARISON,
  deleteComparison,
  getComparisonById,
  loadComparison,
  removeMissingComparisonData,
  rerunComparison,
  skipEmptyState,
} from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';
import { Household } from '@/types/households';
import Comparison from '@/types/comparison';
import ComparisonContact from '@/types/comparison-contact';
import GenericContact from '@/types/generic-contact';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { formatEventDate } from '@/utils/time';
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { COMPARISON_NAV_PATHS, NAV_PATHS } from '@/utils/constants';
import { getValueColumnDef, getCustomColumnDef } from '@/utils/data-table';
import { selectIsLoadingEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { generatePath, useHistory } from 'react-router';
import { getHouseholdComparisons } from '@/redux/modules/household';
import { comparisonHasGifItems, comparisonHasMissingOrModifiedItems } from '@/utils/comparison';
import { getContactById, loadContactData } from '@/redux/modules/contacts';
import { resetGif } from '@/redux/modules/common/actionCreators';
import { LoadingSpinner } from '@/views/components/common';
import { useDebouncedCallback } from 'use-debounce';

type DispatchProps = {
  closeComparison: (comparison: Comparison) => void;
  getContactById: (id: string) => Promise<GenericContact>;
  getComparisonById: (id: string, contact?: ComparisonContact) => Promise<Comparison>;
  getHouseholdComparisons: (query?: string, shouldFetchNext?: boolean) => Promise<ReducedComparison[]>;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  loadContactData: (contact: GenericContact) => Promise<void>;
  skipEmptyState: () => void;
  startLoaderAction: (name: string, params?: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
  removeMissingComparisonData: (comparison: Comparison) => Promise<Comparison>;
  rerunComparison: (comparison: Comparison) => Promise<Comparison>;
  resetGif: () => void;
};

type StateProps = {
  currentHousehold?: Household;
  householdComparisons: ReducedComparison[];
  hasMoreHouseholdComparisons: boolean;
  isDeletingComparison: boolean;
  isLoadingRecentComparisons: boolean;
  locale: Locale;
  loadedComparison?: Comparison;
  user?: Nullable<User>;
};

type Props = DispatchProps & StateProps;

const HouseholdComparisonsTable = (props: Props) => {
  const {
    currentHousehold,
    hasMoreHouseholdComparisons,
    isDeletingComparison,
    isLoadingRecentComparisons,
    householdComparisons,
    locale,
    loadedComparison,
    user,
    closeComparison,
    getContactById,
    getComparisonById,
    getHouseholdComparisons,
    loadComparison,
    loadContactData,
    skipEmptyState,
    startLoaderAction,
    stopLoaderAction,
    removeMissingComparisonData,
    rerunComparison,
    resetGif,
  } = props;

  const [comparisonSearchQuery, setComparisonSearchQuery] = useState<string>('');
  const [debouncingSearch, setDebouncingSearch] = useState<boolean>(false);
  const [selectedComparisonsForDelete, setSelectedComparisonsForDelete] = useState<ReducedComparison[]>([]);
  const [showDeleteComparisonsModal, setShowDeleteComparisonsModal] = useState<boolean>(false);
  const [showComparisonMissingItemsModal, setShowComparisonMissingItemsModal] = useState<boolean>(false);

  const i18nBasePath = 'ComparisonsTableV2';
  const history = useHistory();

  useEffect(() => {
    clearSearch();
    return () => {
      debouncedSearch.cancel();
      debouncedSearch('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ACTIONS

  const onOpenComparison = async (comparison: ReducedComparison) => {
    if (!comparison._id || !currentHousehold) {
      return;
    }

    startLoaderAction('OPEN_COMPARISON', { comparisonId: comparison._id });

    const openedComparison = await getComparisonById(comparison._id, comparison.householdId ? undefined : comparison.contact);

    if (!openedComparison) {
      return;
    }

    const loadedComparison = await loadComparison(openedComparison);

    if (comparisonHasMissingOrModifiedItems(loadedComparison)) {
      setShowComparisonMissingItemsModal(true);
      stopLoaderAction('OPEN_COMPARISON');
      return;
    }

    heapUtils.track(HEAP_CUSTOM_EVENTS.LOAD_EXISTING_COMPARISON);

    stopLoaderAction('OPEN_COMPARISON');

    const pathname = generatePath(NAV_PATHS.COMPARISON_DETAILS, { comparisonId: comparison._id || '' });
    const contactSearch = comparison.contact?._id ? `?contact=${comparison.contact?._id}` : '';

    if (comparisonHasGifItems(loadedComparison)) {
      skipEmptyState();
      resetGif();
      history.push({
        pathname: `${pathname}${COMPARISON_NAV_PATHS.GIF}`,
        search: contactSearch,
      });
      return;
    }

    const originPath = generatePath(NAV_PATHS.HOUSEHOLD_COMPARISONS, { householdId: currentHousehold.id });
    history.push({ pathname, search: contactSearch, state: { from: { pathname: originPath } } });
  };

  const removeMissingItemsAndLoadComparison = async () => {
    const originalComparison = loadedComparison;
    if (!originalComparison) {
      return;
    }

    let newComparison = await removeMissingComparisonData(originalComparison);
    newComparison = await rerunComparison(newComparison);

    closeComparison(originalComparison);
    await loadComparison(newComparison);

    if (newComparison.contact && newComparison.contact._id) {
      const contact = await getContactById(newComparison.contact._id);
      loadContactData(contact);
    }

    setShowComparisonMissingItemsModal(false);
    history.push(NAV_PATHS.COMPARISON);
  };

  const onLoadMore = async () => {
    debouncedSearch(comparisonSearchQuery);
  };

  // PAGE UI/DATA FORMATTING

  const formatLastModifiedDate = (value: string | Date): JSX.Element => <>{value ? formatEventDate(new Date(value)) : ''}</>;

  const renderMultipleContacts = (info: any) => {
    if (!info.row.original.contacts?.length) {
      return <></>;
    }
    return (
      <div className="contact-labels-container">
        {info.row.original.contacts.map((contact: any, index: number) => (
          <ContactLabel key={index} contact={contact} showNameLabel={false} showTooltip />
        ))}
      </div>
    );
  };

  const getTableColumns: ColumnDef<ReducedComparison>[] = useMemo(
    () => [
      getValueColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.NAME, i18nBasePath),
      getCustomColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.CONTACTS, i18nBasePath, info => renderMultipleContacts(info)),
      getValueColumnDef(COMPARISON_TABLE_COLUMNS_KEYS.LAST_MODIFIED, i18nBasePath, undefined, formatLastModifiedDate),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  // SEARCH FUNCTIONALITY

  const clearSearch = () => {
    setComparisonSearchQuery('');
    debouncedSearch('');
  };

  const debouncedSearch = useDebouncedCallback((query: string) => {
    if (!currentHousehold) {
      return;
    }
    getHouseholdComparisons(query).then(() => {
      setDebouncingSearch(false);
    });
  }, 300);

  const onSearchComparison = (query: string) => {
    debouncedSearch.cancel();
    setDebouncingSearch(false);

    if (!query) {
      clearSearch();
    }

    setComparisonSearchQuery(query);
    setDebouncingSearch(true);
    debouncedSearch(query);
  };

  // NAVIGATION
  const createComparison = () => {
    let originPath;
    if (currentHousehold && currentHousehold.id) {
      originPath = generatePath(NAV_PATHS.HOUSEHOLD_COMPARISONS, { householdId: currentHousehold?.id });
    }
    history.push({ pathname: NAV_PATHS.COMPARISON, state: { from: { pathname: originPath } } });
  };

  // PAGE CONTENT BUILDER

  const renderHeaderActions = (): JSX.Element => {
    let headerElement: JSX.Element = <></>;

    if (householdComparisons?.length || comparisonSearchQuery) {
      headerElement = (
        <div className="household-comparisons-header-actions">
          <SearchInput
            className="search-input-v2"
            onChange={onSearchComparison}
            placeholder={formatMessage('Households.Comparisons.SearchPlaceholder')}
            rightSection={comparisonSearchQuery ? <i className="icon-cross-thin clear-term" onClick={clearSearch} /> : undefined}
            value={comparisonSearchQuery}
            loading={debouncingSearch}
          />
          {userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_CREATE) && (
            <Button variant="primary" icon="add" onClick={createComparison} data-testid="household-details-create-comparisons">
              <FormattedMessage id="Households.Comparisons.CreateComparison" />
            </Button>
          )}
        </div>
      );
    }

    return headerElement;
  };

  const renderNoSearchResultsComponent = (): JSX.Element => (
    <EmptySearchResults
      searchQuery={comparisonSearchQuery}
      titleToLocalize="ComparisonsTableV2.EmptyData.NoResults.Title"
      messageToLocalize="ComparisonsTableV2.EmptyData.NoResults.Message"
    />
  );

  const getMenuItems = <T extends ReducedComparison>(): MenuItem<T>[] => [
    {
      key: 'delete-comparison',
      className: 'destructive',
      icon: 'icon-ds-delete',
      value: formatMessage('ComparisonsTable.Menu.DeleteComparison'),
      onClick: (value?: ReducedComparison) => {
        if (value) {
          setSelectedComparisonsForDelete([value]);
        }
        return setShowDeleteComparisonsModal(true);
      },
      isAllowed: (value: ReducedComparison) => value?.availableActions?.delete ?? false,
    },
  ];

  const renderContent = () => {
    const shouldRenderEmptyState = (!householdComparisons || householdComparisons.length <= 0) && !comparisonSearchQuery;
    const primaryButton = {
      label: 'EmptyStateCard.Comparisons.PrimaryButtonText',
      onClick: createComparison,
    };

    return (
      <div className="household-details-comparisons-container" data-testid="household-details-comparisons-container">
        <div className="household-details-comparisons-header">
          <h3 className="household-details-comparisons-header-title">
            <FormattedMessage id="Households.Comparisons.Title" />
          </h3>
          {renderHeaderActions()}
        </div>
        <div className="household-details-comparisons-body">
          {shouldRenderEmptyState && (
            <EmptyStateCard
              i18nVariables={{ householdName: props.currentHousehold?.name || '' }}
              messagePath="EmptyStateCard.Households.Comparisons.Message"
              primaryButton={primaryButton}
              titlePath="EmptyStateCard.Households.Comparisons.Title"
            />
          )}

          {!shouldRenderEmptyState && isLoadingRecentComparisons && <LoadingSpinner />}

          {!shouldRenderEmptyState && !isLoadingRecentComparisons && (
            <DataTableV2
              columns={getTableColumns}
              data={householdComparisons}
              enableSorting
              infiniteScrollOptions={{
                show: hasMoreHouseholdComparisons ?? false,
                onEnter: onLoadMore,
              }}
              menuItems={getMenuItems()}
              noDataComponent={renderNoSearchResultsComponent}
              onRowClick={onOpenComparison}
            />
          )}
        </div>
      </div>
    );
  };

  // MODAL ACTIONS
  const onConfirmDeleteComparison = async () => {
    setShowDeleteComparisonsModal(false);

    // TODO: Household epic https://capintel.atlassian.net/browse/CONN-7328
    //* * While the data is being mocked during development of the Household feature, the list of comparisons is unimpacted by this delete action
    //* * Test again once we are using real data
    return Promise.all(selectedComparisonsForDelete.map(reducedComparison => deleteComparison(reducedComparison))).then(() => {
      setShowDeleteComparisonsModal(false);
    });
  };

  return (
    <>
      {renderContent()}
      {showDeleteComparisonsModal && (
        <DeleteComparisonsModal
          isDeleting={isDeletingComparison}
          comparisons={selectedComparisonsForDelete}
          show={showDeleteComparisonsModal}
          onCancel={() => setShowDeleteComparisonsModal(false)}
          onConfirm={onConfirmDeleteComparison}
        />
      )}
      {showComparisonMissingItemsModal && loadedComparison && (
        <ComparisonMissingItemsModal
          show={showComparisonMissingItemsModal}
          onCancel={() => {
            setShowComparisonMissingItemsModal(false);
          }}
          onConfirm={removeMissingItemsAndLoadComparison}
          comparison={loadedComparison}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentHousehold: state.households.currentHousehold,
  hasMoreHouseholdComparisons: state.households.hasMore.comparisons,
  householdComparisons: state.households.comparisons,
  isDeletingComparison: selectIsLoadingEvery(state, [DELETE_COMPARISON.type]),
  isLoadingRecentComparisons: selectIsLoadingSome(state, ['OPEN_COMPARISON']),
  loadedComparison: state.comparison.activeComparison,
  locale: state.intl.locale,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  closeComparison,
  getComparisonById,
  getContactById,
  getHouseholdComparisons,
  loadComparison,
  loadContactData,
  removeMissingComparisonData,
  rerunComparison,
  resetGif,
  skipEmptyState,
  startLoaderAction,
  stopLoaderAction,
})(withErrorBoundary(HouseholdComparisonsTable));
